import React, { useState, useEffect } from 'react';
import { dashboardApi, documentsApi } from '../../services/api';
import { Card, CardContent } from '../../components/ui/card';
import { Users, Chalkboard, ClipboardText, Certificate, Buildings, FilePdf, DownloadSimple } from '@phosphor-icons/react';
import { toast } from 'sonner';
import WorkerRutSearch from '../../components/WorkerRutSearch';

export default function ContractorDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, docsRes] = await Promise.all([
          dashboardApi.contractor(),
          documentsApi.list(),
        ]);
        setStats(statsRes.data);
        setDocs(docsRes.data);
      } catch (error) {
        toast.error('Error al cargar dashboard');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2BBAA4]" />
      </div>
    );
  }

  const statCards = [
    { label: 'Trabajadores', value: stats?.total_workers || 0, icon: Users, desc: 'En tu empresa' },
    { label: 'Capacitaciones', value: stats?.total_trainings || 0, icon: Chalkboard, desc: `${stats?.pending_trainings || 0} pendientes` },
    { label: 'Inscripciones', value: stats?.total_enrollments || 0, icon: ClipboardText, desc: 'Total inscripciones' },
    { label: 'Certificados', value: stats?.total_certificates || 0, icon: Certificate, desc: 'Emitidos' },
  ];

  const handleDownload = (doc) => {
    const token = localStorage.getItem('token');
    const url = documentsApi.download(doc.id);
    window.open(`${url}?token=${token}`, '_blank');
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="contractor-dashboard">
      <div>
        <h1 className="text-2xl font-bold text-[#2B4B8C]">Dashboard Contratista</h1>
        <div className="flex items-center gap-2 mt-1">
          <Buildings size={18} className="text-[#2BBAA4]" />
          <p className="text-[#2B4B8C]/60">
            Mandante: <span className="font-medium text-[#2B4B8C]">{stats?.mandante_name || 'Sin asignar'}</span>
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card key={stat.label} className="bg-white border-[#2B4B8C]/10">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium text-[#2B4B8C]/50 uppercase tracking-wider">{stat.label}</p>
                  <p className="text-3xl font-bold text-[#2B4B8C] mt-1">{stat.value}</p>
                  <p className="text-sm text-[#2B4B8C]/50 mt-1">{stat.desc}</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-[#2BBAA4]/10 flex items-center justify-center">
                  <stat.icon size={24} className="text-[#2BBAA4]" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Documents Section */}
      {docs.length > 0 && (
        <Card className="bg-white border-[#2B4B8C]/10" data-testid="contractor-documents">
          <CardContent className="p-6">
            <h2 className="text-base font-bold text-[#2B4B8C] mb-4 flex items-center gap-2">
              <FilePdf size={20} className="text-red-500" /> Documentos
            </h2>
            <div className="space-y-2">
              {docs.map(d => (
                <div
                  key={d.id}
                  className="flex items-center justify-between p-3 rounded-lg border border-[#2B4B8C]/5 hover:bg-[#f8fafc] transition-colors"
                  data-testid={`contractor-doc-${d.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <FilePdf size={22} className="text-red-500 shrink-0" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#2B4B8C] truncate">{d.name}</p>
                      <p className="text-[10px] text-[#2B4B8C]/40">
                        {d.created_at ? new Date(d.created_at).toLocaleDateString('es-CL') : ''}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDownload(d)}
                    className="flex items-center gap-1 text-xs font-medium text-[#2BBAA4] hover:text-[#239986] shrink-0 px-3 py-1.5 rounded-lg hover:bg-[#2BBAA4]/10 transition-colors"
                    data-testid={`contractor-download-${d.id}`}
                  >
                    <DownloadSimple size={14} /> Descargar
                  </button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* RUT Search */}
      <WorkerRutSearch />
    </div>
  );
}
